import { useSyncExternalStore } from 'react'

/**
 * The one piece of state the composer and Clean View share while searching.
 *
 * The composer (App.tsx) owns the MODE and the QUERY: pressing the search button turns
 * the text box into the query box and every keystroke lands here. Clean View
 * (AgentPanel's TranscriptView) owns the ANSWER: it counts the items that hold a hit,
 * reports the count, and shows whichever one is current. Neither renders the other, so
 * a module store — the same shape as billing/plan.ts — is the seam, and App does not
 * re-render per keystroke (the uncontrolled-composer rule): only the strip and the view
 * subscribe.
 */
export type SearchState = {
  /** Search mode is on: the composer is the query box and Clean View marks hits. */
  active: boolean
  query: string
  /** How many rendered items hold a hit — reported by the view, never guessed here. */
  count: number
  /** Which of them is shown, 0-based in transcript order; -1 while there is none. */
  current: number
  /**
   * WHAT WAS SEARCHED — owner, 2026-09-28: the search asks the machine's edge, which
   * reads the whole conversation; `loaded` is the fallback when it cannot (an edge from
   * before the route, a machine that did not answer), and `pending` while it is asked.
   */
  scope: 'conversation' | 'loaded' | 'pending'
  /** Matches in the whole conversation — above `count` when only the newest were returned. */
  total: number
  /** A block index to land on once the matches arrive (the global search's tapped result). */
  target: number | null
  /** The current match is older than the view can load: its text, shown instead. */
  older: { index: number; text: string; ts?: number } | null
}

const OFF: SearchState = {
  active: false, query: '', count: 0, current: -1, scope: 'loaded', total: 0, target: null, older: null,
}
let state: SearchState = OFF
const listeners = new Set<() => void>()

function set(next: SearchState): void {
  state = next
  for (const l of listeners) l()
}

export function openSearch(): void {
  if (!state.active) set({ ...OFF, active: true })
}

export function closeSearch(): void {
  if (state.active) set(OFF)
}

/** A new query forgets the old answer; the view reports the new one. */
export function setSearchQuery(query: string): void {
  if (!state.active || query === state.query) return
  set({ ...state, query, count: 0, current: -1, total: 0, older: null })
}

/** Land on this block once the matches arrive — cleared when it has been applied. */
export function setSearchTarget(index: number | null): void {
  if (!state.active || state.target === index) return
  set({ ...state, target: index })
}

/**
 * The edge's answer: how many matches are navigable (`count`), how many the whole
 * conversation holds (`total`), what was searched, and — if a target is pending and
 * among them — which one to show first.
 */
export function reportSearch(r: { count: number; total: number; scope: SearchState['scope']; targetAt?: number }): void {
  if (!state.active) return
  const landed = r.targetAt !== undefined && r.targetAt >= 0
  const inRange = state.current >= 0 && state.current < r.count
  const current = r.count === 0 ? -1 : landed ? r.targetAt! : inRange ? state.current : r.count - 1
  const target = landed ? null : state.target
  if (
    r.count === state.count && current === state.current && r.total === state.total &&
    r.scope === state.scope && target === state.target
  ) return
  set({ ...state, count: r.count, current, total: r.total, scope: r.scope, target })
}

export function setSearchOlder(older: SearchState['older']): void {
  if (!state.active) return
  if (older?.index === state.older?.index && !!older === !!state.older) return
  set({ ...state, older })
}

/**
 * The view says how many items hold a hit. A fresh answer lands on the NEWEST match
 * (the last one), the way a reader looks back from the bottom of a conversation; a
 * count that merely grew while the agent kept writing leaves the reader where they are.
 */
export function reportHits(count: number): void {
  if (!state.active) return
  const inRange = state.current >= 0 && state.current < count
  const current = count === 0 ? -1 : inRange ? state.current : count - 1
  if (count === state.count && current === state.current && state.scope === 'loaded' && state.total === count) return
  set({ ...state, count, current, scope: 'loaded', total: count })
}

/** −1 = older (up the transcript), +1 = newer; wraps at both ends. */
export function stepSearch(delta: 1 | -1): void {
  if (!state.active || state.count === 0) return
  set({ ...state, current: (state.current + delta + state.count) % state.count, older: null })
}

export function searchState(): SearchState {
  return state
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function useSearch(): SearchState {
  return useSyncExternalStore(subscribe, searchState, searchState)
}
